import { Link } from 'react-router-dom';
import { School, Zap } from 'lucide-react';
import { FavoriteStar } from '../lists';
import type { CustomerStatus, PersonListItem } from '../../types';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import { formatSportLabel } from '../../lib/sportLabel';
import { TemperatureIcon } from '../shared/temperatureConfig';
import { AssignmentBadge } from '../shared/AssignmentBadge';
import { CustomerStatusBadge } from '../organizations/CustomerStatusBadge';

// Border-left color keyed off the primary org's customerStatus
const BORDER: Record<CustomerStatus, string> = {
  PROSPECT: 'border-l-gray-400 dark:border-l-gray-500',
  ONBOARDED: 'border-l-blue-500',
  ACTIVE: 'border-l-green-500',
  SLIPPING: 'border-l-amber-500',
  LAPSED: 'border-l-red-500',
};

// Compact relative time for the meta line (no "Last activity:" prefix — it
// sits alongside the assignment + Blast indicators).
function relativeActivity(dateString: string | null): string {
  if (!dateString) return 'No activity';
  const diffDays = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function formatFollowUpDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isOverdue(dateString: string): boolean {
  const date = new Date(dateString);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return date < now;
}

const Separator = () => (
  <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">
    ·
  </span>
);

interface PersonCardProps {
  person: PersonListItem;
  currentUserId: string;
}

export const PersonCard = ({ person, currentUserId }: PersonCardProps) => {
  const primaryOrg = person.organizations?.[0];
  const orgStatus: CustomerStatus | undefined = primaryOrg?.organization.customerStatus ?? undefined;
  const borderColor = orgStatus ? BORDER[orgStatus] : 'border-l-gray-300 dark:border-l-gray-600';

  // Row 2: role · sport (e.g. "Head Coach · Girls Basketball"). Co-ed sports
  // drop the gender word (formatSportLabel / CRM-25). Omitted when both empty.
  const sportLabel = primaryOrg?.sport
    ? formatSportLabel(primaryOrg.sport.gender, primaryOrg.sport.name)
    : null;
  const roleSport = [person.position, sportLabel].filter(Boolean).join(' · ');

  // Row 3: school + city/state. School truncates; "City, ST" stays visible.
  const school = primaryOrg?.organization.name;
  const location = [primaryOrg?.organization.city, primaryOrg?.organization.state]
    .filter(Boolean)
    .join(', ');

  return (
    <Link to={`/people/${person.id}`} className="block">
      <div
        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow border-l-4 ${borderColor}`}
      >
        {/* Row 1: Name + Customer status */}
        <div className="flex justify-between items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5 min-w-0">
            {person.temperature && (
              <TemperatureIcon temperature={person.temperature} className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate">
              {effectiveFirstName(person)} {effectiveLastName(person)}
            </span>
          </span>
          <span className="flex-shrink-0 flex items-center gap-1">
            {orgStatus && <CustomerStatusBadge status={orgStatus} size="sm" />}
            <FavoriteStar kind="PERSON" id={person.id} size={20} />
          </span>
        </div>

        {/* Row 2: Role · sport */}
        {roleSport && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">{roleSport}</div>
        )}

        {/* Row 3: School + city/state (school truncates, location stays) */}
        {primaryOrg && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mt-1 min-w-0">
            <School className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{school}</span>
            {location && (
              <>
                <Separator />
                <span className="flex-shrink-0">{location}</span>
              </>
            )}
          </div>
        )}

        {/* Row 4: Assignment · last activity · Blast */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-2">
          <AssignmentBadge
            assignedUserId={person.assignedUserId}
            assignedUserName={person.assignedUser?.displayName ?? null}
            currentUserId={currentUserId}
          />
          <Separator />
          <span className="truncate">{relativeActivity(person.lastActivityAt)}</span>
          {person.blastUserId && (
            <>
              <Separator />
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                <Zap className="h-3 w-3" />
                In Blast
              </span>
            </>
          )}
        </div>

        {/* Row 5: Follow-up (conditional) */}
        {person.nextFollowUpAt && (
          <div
            className={`text-xs mt-1 ${
              isOverdue(person.nextFollowUpAt) ? 'text-red-500' : 'text-amber-600 dark:text-amber-500'
            }`}
          >
            Follow up: {formatFollowUpDate(person.nextFollowUpAt)}
          </div>
        )}
      </div>
    </Link>
  );
};
